import type {
  HandlerFunction,
  DocumentUpdateMessage,
  DocumentDeletedMessage,
  DocumentRestoredMessage,
  EntityMessageType,
} from "../types";

/**
 * Entity message handler
 *
 * Handles real-time notifications for document updates, deletions, and restorations.
 * Used for conflict detection when multiple users edit the same document.
 *
 * This handler:
 * 1. Updates the conflict store with the latest remote version
 * 2. Shows non-blocking toast notifications to alert the user
 * 3. Provides actions for the user to refresh or navigate away
 */
export const handleEntityMessage: HandlerFunction = (data) => {
  const toast = useToast();
  const conflictStore = useEntityConflictStore();
  const message = data as { type: EntityMessageType };

  switch (message.type) {
    case "document.updated": {
      const payload = data as DocumentUpdateMessage;

      // Track the latest version for conflict detection
      conflictStore.setRemoteVersion(
        payload.entityType,
        payload.entityId,
        payload.version,
        payload.updatedBy,
      );

      // Non-blocking toast notification with refresh action
      toast.add({
        title: "Document Updated",
        description: `${payload.updatedBy.name} made changes to this document`,
        color: "info",
        icon: "i-lucide-file-edit",
        duration: 0, // Don't auto-dismiss - user must acknowledge
        actions: [
          {
            label: "Refresh",
            click: () => {
              conflictStore.refreshDocument(payload.entityType, payload.entityId);
            },
          },
          {
            label: "Dismiss",
          },
        ],
      });
      break;
    }

    case "document.deleted": {
      const payload = data as DocumentDeletedMessage;

      // Mark as deleted in the conflict store
      conflictStore.markDeleted(payload.entityType, payload.entityId);

      // Warning toast with navigation action
      toast.add({
        title: "Document Deleted",
        description: "This document has been deleted by another user",
        color: "warning",
        icon: "i-lucide-trash",
        duration: 0,
        actions: [
          {
            label: "Go to list",
            click: () => {
              // Navigate to the appropriate list page
              navigateTo(`/${payload.entityType}s`);
            },
          },
        ],
      });
      break;
    }

    case "document.restored": {
      const payload = data as DocumentRestoredMessage;

      // Update the version in the conflict store
      conflictStore.setRemoteVersion(
        payload.entityType,
        payload.entityId,
        payload.revisionNumber,
        payload.restoredBy,
      );

      // Info toast about the restoration
      toast.add({
        title: "Document Restored",
        description: `Restored to revision ${payload.restoredFrom} by ${payload.restoredBy.name}`,
        color: "success",
        icon: "i-lucide-history",
        duration: 5000, // Auto-dismiss after 5 seconds
      });
      break;
    }

    default:
      console.log("[Transmit] Unknown entity message type:", message.type, data);
  }
};
